import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangleIcon, GavelIcon, LockIcon, SearchCheckIcon, ShieldAlertIcon } from 'lucide-react';
import PageCalloutRow, { PageCalloutProps } from '@/components/PageCalloutRow';
import {
    HistorySearchBox,
    HistorySearchBoxReturnStateType,
    SEARCH_ANY_STRING,
    availableSearchTypes,
} from './HistorySearchBox';
import HistoryTable from './HistoryTable';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HistoryStatsResp, HistoryTableSearchType } from '@shared/historyApiTypes';
import { useBackendApi } from '@/hooks/fetch';
import { createRandomHslColor } from '@/lib/utils';
import { LocalStorageKey } from '@/lib/localStorage';


//Memoized components
const HistorySearchBoxMemo = memo(HistorySearchBox);
const HistoryTableMemo = memo(HistoryTable);
const PageCalloutRowMemo = memo(PageCalloutRow);

//Helpers for storing search and filters in URL
const updateUrlSearchParams = (
    search: HistoryTableSearchType,
    filterbyType: string | undefined,
    filterbyAdmin: string | undefined,
) => {
    const newUrl = new URL(window.location.toString());
    if (search && search.type && search.value) {
        newUrl.searchParams.set("searchType", search.type);
        newUrl.searchParams.set("searchQuery", search.value);
    } else {
        newUrl.searchParams.delete("searchType");
        newUrl.searchParams.delete("searchQuery");
    }
    if (filterbyType && filterbyType !== '!any') {
        newUrl.searchParams.set("filterbyType", filterbyType);
    } else {
        newUrl.searchParams.delete("filterbyType");
    }
    if (filterbyAdmin && filterbyAdmin !== '!any') {
        newUrl.searchParams.set("filterbyAdmin", filterbyAdmin);
    } else {
        newUrl.searchParams.delete("filterbyAdmin");
    }
    window.history.replaceState({}, "", newUrl);
}
const getStoredSearchType = () => {
    const stored = localStorage.getItem(LocalStorageKey.HistoryPageSearchType);
    if (!stored) return false;
    if (!availableSearchTypes.some(f => f.value === stored)) return false;
    return stored;
}
const setStoredSearchType = (searchType: string | false) => {
    if (searchType) {
        localStorage.setItem(LocalStorageKey.HistoryPageSearchType, searchType);
    } else {
        localStorage.removeItem(LocalStorageKey.HistoryPageSearchType);
    }
}

const getInitialState = () => {
    const params = new URLSearchParams(window.location.search);
    //NOTE: unlike the PlayersPage, I'm not really validating the filters here
    const validSearchTypes = availableSearchTypes.map(f => f.value) as string[];
    const searchType = params.get('searchType');
    const searchQuery = params.get('searchQuery');
    let defaultSearchType = availableSearchTypes[0].value as string;
    let rememberSearchType = false;
    try {
        const storedSearchType = getStoredSearchType();
        if (storedSearchType) {
            defaultSearchType = storedSearchType;
            rememberSearchType = true;
        }
    } catch (error) {
        console.error('Failed to get stored search type:', error);
    }

    return {
        search: searchQuery && searchType && validSearchTypes.includes(searchType) ? {
            type: searchType,
            value: searchQuery,
        } : {
            type: defaultSearchType,
            value: '',
        },
        filterbyType: params.get('filterbyType') ?? SEARCH_ANY_STRING,
        filterbyAdmin: params.get('filterbyAdmin') ?? SEARCH_ANY_STRING,
        rememberSearchType,
    };
}


//which set of counters the callout row shows
type CalloutSet = 'warns' | 'jails' | 'bans';

export default function HistoryPage() {
    const [calloutData, setCalloutData] = useState<HistoryStatsResp | undefined>(undefined);
    const [calloutSet, setCalloutSet] = useState<CalloutSet>('warns');
    const [searchBoxReturn, setSearchBoxReturn] = useState<HistorySearchBoxReturnStateType | undefined>(undefined);
    const statsApi = useBackendApi<HistoryStatsResp>({
        method: 'GET',
        path: '/history/stats',
        abortOnUnmount: true,
    });

    //Callout data
    useEffect(() => {
        statsApi({
            success(data, toastId) {
                setCalloutData(data);
            },
        })
    }, []);

    //HistorySearchBox handlers
    const doSearch = useCallback((
        search: HistoryTableSearchType,
        filterbyType: string | undefined,
        filterbyAdmin: string | undefined,
        rememberSearchType: boolean,
    ) => {
        setSearchBoxReturn({ search, filterbyType, filterbyAdmin });
        updateUrlSearchParams(search, filterbyType, filterbyAdmin);
        try {
            setStoredSearchType(rememberSearchType ? search.type : false);
        } catch (error) {
            console.error('Failed to set stored search type:', error);
        }
    }, []);
    const initialState = useMemo(getInitialState, []);

    const calloutRowData = useMemo(() => {
        const hasCalloutData = calloutData && !('error' in calloutData);
        if (calloutSet === 'bans') {
            return [
                {
                    label: 'Total Bans',
                    value: hasCalloutData ? calloutData.totalBans : false,
                    icon: <GavelIcon />,
                },
                {
                    label: 'Bans 7d',
                    value: hasCalloutData ? calloutData.bansLast7d : false,
                    icon: <GavelIcon />,
                    prefix: '+'
                },
                {
                    label: 'Bans 30d',
                    value: hasCalloutData ? calloutData.bansLast30d : false,
                    icon: <GavelIcon />,
                    prefix: '+'
                },
            ] satisfies PageCalloutProps[];
        }
        //GMW: there is no anticheat or jail backend here, so there is nothing to
        //count. The tab is gone too - this only catches a value left in storage.
        if (calloutSet === 'jails') {
            return [] satisfies PageCalloutProps[];
        }
        return [
            {
                label: 'Total Warns',
                value: hasCalloutData ? calloutData.totalWarns : false,
                icon: <AlertTriangleIcon />,
            },
            {
                label: 'Warns 7d',
                value: hasCalloutData ? calloutData.warnsLast7d : false,
                icon: <AlertTriangleIcon />,
                prefix: '+'
            },
            {
                label: 'Warns 30d',
                value: hasCalloutData ? calloutData.warnsLast30d : false,
                icon: <AlertTriangleIcon />,
                prefix: '+'
            },
        ] satisfies PageCalloutProps[];
    }, [calloutData, calloutSet]);

    return (<div
        className='flex flex-col min-w-96 w-full h-contentvh'
    >
        {/* <div
            //DEBUG component state
            className='w-full bg-black p-2'
            style={{ color: createRandomHslColor() }}
        >{JSON.stringify(searchBoxReturn)}</div> */}

        <Tabs
            value={calloutSet}
            onValueChange={(value) => setCalloutSet(value as CalloutSet)}
            className="px-2 md:px-0 mb-2 md:mb-3"
        >
            <TabsList>
                <TabsTrigger value="warns">Warns</TabsTrigger>
                <TabsTrigger value="bans">Bans</TabsTrigger>
            </TabsList>
        </Tabs>

        <PageCalloutRowMemo callouts={calloutRowData} />

        {calloutData && !('error' in calloutData) ? (
            <HistorySearchBoxMemo
                doSearch={doSearch}
                initialState={initialState}
                adminStats={calloutData.groupedByAdmins}
            />
        ) : null}

        {searchBoxReturn ? (
            <HistoryTableMemo
                search={searchBoxReturn.search}
                filterbyType={searchBoxReturn.filterbyType}
                filterbyAdmin={searchBoxReturn.filterbyAdmin}
            />
        ) : null}
    </div>);
}
